import React, {useEffect, useState, useRef} from 'react';
import {useParams, useSearchParams, useNavigate} from 'react-router-dom';
import {
    Box,
    Text,
    Image,
    VStack,
    HStack,
    Card,
    CardHeader,
    CardBody,
    Flex,
    Button,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    useCheckbox,
    useCheckboxGroup,
    useRadio,
    useRadioGroup,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Select,
    Stack,
    useToast,
    IconButton,
} from '@chakra-ui/react';
import {FaTrash} from 'react-icons/fa';
import {customAuthAxios} from '../../../utils/customAuthAxios';
import {getDjangoToken} from '../../../utils/instagram/services/apiService';

// CustomCheckboxコンポーネントの定義
const CustomCheckbox = (props: any) => {
    const { state, getInputProps, getCheckboxProps } = useCheckbox(props);

    return (
        <Box as="label">
            <input {...getInputProps()} hidden/>
            <Box
                {...getCheckboxProps()}
                px={4}
                py={2}
                borderWidth="1px"
                borderRadius="full"
                cursor="pointer"
                bg={state.isChecked ? "blue.100" : "white"}
                color={state.isChecked ? "blue.600" : "black"}
                fontWeight={state.isChecked ? "bold" : "normal"}
                transition="all 0.2s"
                _hover={{bg: "blue.50"}}
            >
                {props.children}
            </Box>
        </Box>
    );
};

// RadioCardコンポーネントの定義
const RadioCard = (props: any) => {
    const { radioProps, ...rest } = props;
    const { state, getInputProps, getRadioProps, htmlProps } = useRadio(radioProps);

    return (
        <Box as="label">
            <input {...getInputProps()} hidden/>
            <Box
                {...getRadioProps()}
                px={4}
            py={2}
                borderWidth="1px"
                borderRadius="full"
                cursor="pointer"
                bg={state.isChecked ? "blue.100" : "white"}
                color={state.isChecked ? "blue.600" : "black"}
                fontWeight={state.isChecked ? "bold" : "normal"}
                transition="all 0.2s"
                _hover={{bg: "blue.50"}}
            >
                {props.children}
            </Box>
        </Box>
    );
};

interface InstagramDmContent {
    id: number;
    type: number;
    message: string;
    image: string;
    alert_message: string;
    url: string;
    start_at: string;
    end_at: string;
}

const InstagramDmCreate: React.FC = () => {
    const {restaurantId} = useParams<{ restaurantId: string }>();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('edit');
    const navigate = useNavigate();
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState<InstagramDmContent>({
        id: 0,
        type: 1,
        message: '',
        image: '',
        alert_message: '',
        url: '',
        start_at: '',
        end_at: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [url, setUrl] = useState('');
    const [selectedType, setSelectedType] = useState('メッセージ');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);

    // クーポン用の状態
    const [couponNote, setCouponNote] = useState('');
    const [hasPeriod, setHasPeriod] = useState('no'); // 'yes' or 'no'
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // 抽選用の状態
    const [drawContent, setDrawContent] = useState('');
    const [drawMaxWinners, setDrawMaxWinners] = useState<number>(1);
    const [winningProbability, setWinningProbability] = useState<number>(100);
    const [drawStartDate, setDrawStartDate] = useState('');
    const [drawEndDate, setDrawEndDate] = useState('');
    const [winningBenefitType, setWinningBenefitType] = useState('new'); // 'new' or 'existing'
    const [winningBenefitContent, setWinningBenefitContent] = useState('');
    const [selectedWinningBenefits, setSelectedWinningBenefits] = useState<string[]>([]);
    const [losingBenefitContent, setLosingBenefitContent] = useState('');
    const [drawNote, setDrawNote] = useState('');

    // アンケート用の状態
    const [surveyContent, setSurveyContent] = useState('');
    const [selectedSurveySets, setSelectedSurveySets] = useState<string[]>([]);
    const [hasSurveyPeriod, setHasSurveyPeriod] = useState('no');
    const [surveyStartDate, setSurveyStartDate] = useState('');
    const [surveyEndDate, setSurveyEndDate] = useState('');

    const { getRootProps: getTypeRootProps, getRadioProps: getTypeRadioProps } = useRadioGroup({
        name: 'messageType',
        defaultValue: selectedType,
        onChange: setSelectedType,
    });

    const { getRootProps: getPeriodRootProps, getRadioProps: getPeriodRadioProps } = useRadioGroup({
        name: 'hasPeriod',
        defaultValue: hasPeriod,
        onChange: setHasPeriod,
    });

    const { getRootProps: getWinningBenefitRootProps, getRadioProps: getWinningBenefitRadioProps } = useRadioGroup({
        name: 'winningBenefitType',
        defaultValue: winningBenefitType,
        onChange: setWinningBenefitType,
    });

    const { getRootProps: getSurveyPeriodRootProps, getRadioProps: getSurveyPeriodRadioProps } = useRadioGroup({
        name: 'hasSurveyPeriod',
        defaultValue: hasSurveyPeriod,
        onChange: setHasSurveyPeriod,
    });

    useEffect(() => {
        const fetchDmContent = async () => {
            if (!editId) return;

            try {
                const djangoToken = await getDjangoToken();
                const response = await customAuthAxios.get(
                    `/api/restaurants/${restaurantId}/instagram/dm/${editId}/`,
                    {
                        headers: {Authorization: `Bearer ${djangoToken}`},
                    }
                );
                const data = response.data;
                setFormData(data);
                setTitle(data.name || '');
                setContent(data.message || '');
                setUrl(data.url || '');
                setSelectedType(data.type === 1 ? 'メッセージ' : 
                               data.type === 2 ? 'クーポン' : 
                               data.type === 3 ? '抽選' : 'アンケート');
                
                // クーポン関連のデータ
                setCouponNote(data.alert_message || '');
                if (data.start_at && data.end_at) {
                    setHasPeriod('yes');
                    setStartDate(data.start_at.split('T')[0]);
                    setEndDate(data.end_at.split('T')[0]);
                }

                // 抽選関連のデータ
                setDrawContent(data.message || '');
                setDrawMaxWinners(data.max_winners || 1);
                setWinningProbability(data.winning_probability || 100);
                if (data.start_at && data.end_at) {
                    setDrawStartDate(data.start_at.split('T')[0]);
                    setDrawEndDate(data.end_at.split('T')[0]);
                }
                setWinningBenefitContent(data.winning_benefit || '');
                setLosingBenefitContent(data.losing_benefit || '');
                setDrawNote(data.note || '');

                // アンケート関連のデータ
                setSurveyContent(data.message || '');
                if (data.start_at && data.end_at) {
                    setHasSurveyPeriod('yes');
                    setSurveyStartDate(data.start_at.split('T')[0]);
                    setSurveyEndDate(data.end_at.split('T')[0]);
                }
            } catch (error) {
                console.error('Failed to fetch DM content:', error);
                toast({
                    title: 'エラー',
                    description: 'DMコンテンツの取得に失敗しました',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        };

        fetchDmContent();
    }, [restaurantId, editId]);

    const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const newImages = Array.from(files);
            setSelectedImages([...selectedImages, ...newImages]);
            
            newImages.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreviewUrls(prev => [...prev, reader.result as string]);
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            const newImages = Array.from(files).filter(file => file.type.startsWith('image/'));
            setSelectedImages([...selectedImages, ...newImages]);
            
            newImages.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreviewUrls(prev => [...prev, reader.result as string]);
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const handleRemoveImage = (index: number) => {
        setSelectedImages(selectedImages.filter((_, i) => i !== index));
        setImagePreviewUrls(imagePreviewUrls.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const djangoToken = await getDjangoToken();
            if (editId) {
                await customAuthAxios.put(
                    `/api/restaurants/${restaurantId}/instagram/dm/${editId}/`,
                    formData,
                    {
                        headers: {Authorization: `Bearer ${djangoToken}`},
                    }
                );
            } else {
                await customAuthAxios.post(
                    `/api/restaurants/${restaurantId}/instagram/dm/`,
                    formData,
                    {
                        headers: {Authorization: `Bearer ${djangoToken}`},
                    }
                );
            }

            toast({
                title: '成功',
                description: editId ? 'DMコンテンツを更新しました' : 'DMコンテンツを作成しました',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            navigate(`/restaurants/${restaurantId}/instagram/bot/settings`);
        } catch (error) {
            console.error('Failed to save DM content:', error);
            toast({
                title: 'エラー',
                description: editId ? 'DMコンテンツの更新に失敗しました' : 'DMコンテンツの作成に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const formData = new FormData();
            formData.append('image', file);

            const djangoToken = await getDjangoToken();
            const response = await customAuthAxios.post(
                `/api/restaurants/${restaurantId}/instagram/dm/upload/`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${djangoToken}`,
                    },
                }
            );

            setFormData(prev => ({...prev, image: response.data.image_url}));
        } catch (error) {
            console.error('Failed to upload image:', error);
            toast({
                title: 'エラー',
                description: '画像のアップロードに失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <Box p={4} width="100%">
            <Card marginY={"20px"}>
                <CardHeader>
                    <Flex justify="space-between" alignItems="center">
                        <Text fontWeight="bold" fontSize="lg">
                            {editId ? 'DMコンテンツの編集' : 'DMコンテンツの新規作成'}
                        </Text>
                    </Flex>
                </CardHeader>

                <CardBody>
                    <form onSubmit={handleSubmit}>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel>■DMコンテンツ名<Text as="span" color="red">※</Text></FormLabel>
                                <Input
                                    placeholder="DMコンテンツ名を入力"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    w="50%"
                                    size="sm"
                                    variant="outline"
                                    focusBorderColor="blue.400"
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>■対象投稿<Text as="span" color="red">※</Text></FormLabel>
                                <HStack spacing={4}>
                                    <RadioCard radioProps={getTypeRadioProps({ value: 'メッセージ' })}>
                                        メッセージ
                                    </RadioCard>
                                    <RadioCard radioProps={getTypeRadioProps({ value: 'クーポン' })}>
                                        クーポン
                                    </RadioCard>
                                    <RadioCard radioProps={getTypeRadioProps({ value: '抽選' })}>
                                        抽選
                                    </RadioCard>
                                    <RadioCard radioProps={getTypeRadioProps({ value: 'アンケート' })}>
                                        アンケート
                                    </RadioCard>
                                </HStack>
                            </FormControl>

                            <FormControl>
                                <FormLabel>■追加コンテンツ</FormLabel>
                                <Text fontSize="sm" mb={2}>画像① ※カルーセル可</Text>
                                <Flex gap={4}>
                                    {imagePreviewUrls.map((url, index) => (
                                        <Box
                                            key={index}
                                            position="relative"
                                            width="150px"
                                            height="150px"
                                        >
                                            <Image
                                                src={url}
                                                alt={`選択された画像 ${index + 1}`}
                                                objectFit="cover"
                                                width="100%"
                                                height="100%"
                                                borderRadius="md"
                                            />
                                            <IconButton
                                                aria-label="画像を削除"
                                                position="absolute"
                                                top={2}
                                                right={2}
                                                size="xs"
                                                colorScheme="red"
                                                icon={<FaTrash size="20px" />}
                                                onClick={() => handleRemoveImage(index)}
                                                borderRadius="full"
                                            />
                                        </Box>
                                    ))}
                                    <Box
                                        width="150px"
                                        height="150px"
                                        borderWidth="2px"
                                        borderStyle="dashed"
                                        borderColor="gray.300"
                                        borderRadius="md"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        cursor="pointer"
                                        onClick={() => fileInputRef.current?.click()}
                                        onDrop={handleDrop}
                                        onDragOver={handleDragOver}
                                        _hover={{ borderColor: "blue.400" }}
                                    >
                                        <VStack spacing={2}>
                                            <Text fontSize="xl" color="gray.400">+</Text>
                                            <Text fontSize="sm" color="gray.500">クリックまたはドラッグ＆ドロップで追加</Text>
                                        </VStack>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleImageSelect}
                                            accept="image/*"
                                            multiple
                                            style={{ display: 'none' }}
                                        />
                                    </Box>
                                </Flex>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>■本文</FormLabel>
                                <Textarea
                                    placeholder="本文を入力"
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    w="50%"
                                    h="100px"
                                    resize="vertical"
                                    size="sm"
                                    variant="outline"
                                    focusBorderColor="blue.400"
                                    mb={0}
                                />
                            </FormControl>

                            {!selectedType.includes('クーポン') && (
                                <FormControl>
                                    <FormLabel>■URL</FormLabel>
                                    <Input
                                        placeholder="URLを入力"
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                        w="50%"
                                        size="sm"
                                        variant="outline"
                                        focusBorderColor="blue.400"
                                        mb={0}
                                    />
                                </FormControl>
                            )}

                            {selectedType === 'クーポン' && (
                                <>
                                    <FormControl>
                                        <FormLabel>■注意事項<Text as="span" color="red">※</Text></FormLabel>
                                        <Textarea
                                            placeholder="注意事項を入力"
                                            value={couponNote}
                                            onChange={(e) => setCouponNote(e.target.value)}
                                            w="50%"
                                            h="100px"
                                            resize="vertical"
                                            size="sm"
                                            mb={0}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>■クーポン利用可能期間<Text as="span" color="red">※</Text></FormLabel>
                                        <Stack spacing={4}>
                                            <HStack spacing={4}>
                                                <RadioCard radioProps={getPeriodRadioProps({ value: 'no' })}>
                                                    期間を指定しない
                                                </RadioCard>
                                                <RadioCard radioProps={getPeriodRadioProps({ value: 'yes' })}>
                                                    期間を指定する
                                                </RadioCard>
                                            </HStack>
                                            
                                            {hasPeriod === 'yes' && (
                                                <HStack spacing={4} alignItems="flex-end">
                                                    <FormControl w="25%">
                                                        <FormLabel fontSize="sm">開始日</FormLabel>
                                                        <Input
                                                            type="date"
                                                            value={startDate}
                                                            onChange={(e) => setStartDate(e.target.value)}
                                                            size="sm"
                                                            mb={0}
                                                        />
                                                    </FormControl>
                                                    <Text>～</Text>
                                                    <FormControl w="25%">
                                                        <FormLabel fontSize="sm">終了日</FormLabel>
                                                        <Input
                                                            type="date"
                                                            value={endDate}
                                                            onChange={(e) => setEndDate(e.target.value)}
                                                            size="sm"
                                                            mb={0}
                                                        />
                                                    </FormControl>
                                                </HStack>
                                            )}
                                        </Stack>
                                    </FormControl>
                                </>
                            )}

                            {selectedType === '抽選' && (
                                <>
                                    <FormControl>
                                        <FormLabel>■本文<Text as="span" color="red">※</Text></FormLabel>
                                        <Textarea
                                            placeholder="抽選の本文を入力"
                                            value={drawContent}
                                            onChange={(e) => setDrawContent(e.target.value)}
                                            w="50%"
                                            h="100px"
                                            resize="vertical"
                                            size="sm"
                                            mb={0}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>■当選上限人数<Text as="span" color="red">※</Text></FormLabel>
                                        <HStack>
                                            <NumberInput
                                                value={drawMaxWinners}
                                                onChange={(_, value) => setDrawMaxWinners(value)}
                                                min={1}
                                                w="50%"
                                                size="sm"
                                                mb={0}
                                            >
                                                <NumberInputField />
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper />
                                                    <NumberDecrementStepper />
                                                </NumberInputStepper>
                                            </NumberInput>
                                            <Text>人</Text>
                                        </HStack>
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>■当選確率<Text as="span" color="red">※</Text></FormLabel>
                                        <HStack>
                                            <NumberInput
                                                value={winningProbability}
                                                onChange={(_, value) => setWinningProbability(value)}
                                                min={0}
                                                max={100}
                                                w="50%"
                                                size="sm"
                                                mb={0}
                                            >
                                                <NumberInputField />
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper />
                                                    <NumberDecrementStepper />
                                                </NumberInputStepper>
                                            </NumberInput>
                                            <Text>%</Text>
                                        </HStack>
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>■抽選期間<Text as="span" color="red">※</Text></FormLabel>
                                        <HStack spacing={4} alignItems="flex-end">
                                            <FormControl w="25%">
                                                <FormLabel fontSize="sm">開始日</FormLabel>
                                                <Input
                                                    type="date"
                                                    value={drawStartDate}
                                                    onChange={(e) => setDrawStartDate(e.target.value)}
                                                    size="sm"
                                                    mb={0}
                                                />
                                            </FormControl>
                                            <Text>～</Text>
                                            <FormControl w="25%">
                                                <FormLabel fontSize="sm">終了日</FormLabel>
                                                <Input
                                                    type="date"
                                                    value={drawEndDate}
                                                    onChange={(e) => setDrawEndDate(e.target.value)}
                                                    size="sm"
                                                    mb={0}
                                                />
                                            </FormControl>
                                        </HStack>
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>■当選時特典<Text as="span" color="red">※</Text></FormLabel>
                                        <Stack spacing={4}>
                                            <HStack spacing={4}>
                                                <RadioCard radioProps={getWinningBenefitRadioProps({ value: 'new' })}>
                                                    新規特典作成
                                                </RadioCard>
                                                <RadioCard radioProps={getWinningBenefitRadioProps({ value: 'existing' })}>
                                                    DMコンテンツから選択
                                                </RadioCard>
                                            </HStack>

                                            {winningBenefitType === 'new' ? (
                                                <Textarea
                                                    placeholder="当選時特典の内容を入力"
                                                    value={winningBenefitContent}
                                                    onChange={(e) => setWinningBenefitContent(e.target.value)}
                                                    w="50%"
                                                    h="100px"
                                                    resize="vertical"
                                                    size="sm"
                                                    mb={0}
                                                />
                                            ) : (
                                                <FormControl>
                                                    <FormLabel fontSize="sm">DMコンテンツから抽選特典を選択（複数選択可）</FormLabel>
                                                    <Select
                                                        value={selectedWinningBenefits}
                                                        onChange={(e) => {
                                                            const values = Array.from(e.target.selectedOptions, option => option.value);
                                                            setSelectedWinningBenefits(values);
                                                        }}
                                                        w="50%"
                                                        size="sm"
                                                        multiple
                                                        height="100px"
                                                        mb={0}
                                                    >
                                                        <option value="benefit1">特典1</option>
                                                        <option value="benefit2">特典2</option>
                                                        <option value="benefit3">特典3</option>
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Stack>
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>■落選時特典<Text as="span" color="red">※</Text></FormLabel>
                                        <Textarea
                                            placeholder="落選時特典の内容を入力"
                                            value={losingBenefitContent}
                                            onChange={(e) => setLosingBenefitContent(e.target.value)}
                                            w="50%"
                                            h="100px"
                                            resize="vertical"
                                            size="sm"
                                            mb={0}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>■注意事項<Text as="span" color="red">※</Text></FormLabel>
                                        <Textarea
                                            placeholder="注意事項を入力"
                                            value={drawNote}
                                            onChange={(e) => setDrawNote(e.target.value)}
                                            w="50%"
                                            h="100px"
                                            resize="vertical"
                                            size="sm"
                                            mb={0}
                                        />
                                    </FormControl>
                                </>
                            )}

                            {selectedType === 'アンケート' && (
                                <>
                                    <FormControl>
                                        <FormLabel>■本文<Text as="span" color="red">※</Text></FormLabel>
                                        <Textarea
                                            placeholder="アンケートの本文を入力"
                                            value={surveyContent}
                                            onChange={(e) => setSurveyContent(e.target.value)}
                                            w="50%"
                                            h="100px"
                                            resize="vertical"
                                            size="sm"
                                            mb={0}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>■アンケートセット<Text as="span" color="red">※</Text></FormLabel>
                                        <Select
                                            value={selectedSurveySets}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value && !selectedSurveySets.includes(value)) {
                                                    setSelectedSurveySets([...selectedSurveySets, value]);
                                                }
                                            }}
                                            w="50%"
                                            size="sm"
                                            mb={2}
                                            variant="outline"
                                            focusBorderColor="blue.400"
                                        >
                                            <option value="">アンケートセットを選択</option>
                                            <option value="set1">アンケートセット1</option>
                                            <option value="set2">アンケートセット2</option>
                                            <option value="set3">アンケートセット3</option>
                                        </Select>
                                        <Flex gap={2} wrap="wrap">
                                            {selectedSurveySets.map((set) => (
                                                <Box
                                                    key={set}
                                                    bg="gray.100"
                                                    px={3}
                                                    py={1}
                                                    borderRadius="md"
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={2}
                                                >
                                                    <Text fontSize="sm" mb={0} lineHeight="normal">アンケートセット{set.replace('set', '')}</Text>
                                                    <Button
                                                        size="xs"
                                                        onClick={() => setSelectedSurveySets(selectedSurveySets.filter(s => s !== set))}
                                                        minWidth="16px"
                                                        height="16px"
                                                        p={0}
                                                        borderRadius="full"
                                                        bg="transparent"
                                                        _hover={{ bg: 'gray.200' }}
                                                    >
                                                        ×
                                                    </Button>
                                                </Box>
                                            ))}
                                        </Flex>
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>■アンケート期間<Text as="span" color="red">※</Text></FormLabel>
                                        <Stack spacing={4}>
                                            <HStack spacing={4}>
                                                <RadioCard radioProps={getSurveyPeriodRadioProps({ value: 'no' })}>
                                                    期間を指定しない
                                                </RadioCard>
                                                <RadioCard radioProps={getSurveyPeriodRadioProps({ value: 'yes' })}>
                                                    期間を指定する
                                                </RadioCard>
                                            </HStack>
                                            
                                            {hasSurveyPeriod === 'yes' && (
                                                <HStack spacing={4} alignItems="flex-end">
                                                    <FormControl w="25%">
                                                        <FormLabel fontSize="sm">開始日</FormLabel>
                                                        <Input
                                                            type="date"
                                                            value={surveyStartDate}
                                                            onChange={(e) => setSurveyStartDate(e.target.value)}
                                                            size="sm"
                                                            mb={0}
                                                        />
                                                    </FormControl>
                                                    <Text mb={0}>～</Text>
                                                    <FormControl w="25%">
                                                        <FormLabel fontSize="sm">終了日</FormLabel>
                                                        <Input
                                                            type="date"
                                                            value={surveyEndDate}
                                                            onChange={(e) => setSurveyEndDate(e.target.value)}
                                                            size="sm"
                                                            mb={0}
                                                        />
                                                    </FormControl>
                                                </HStack>
                                            )}
                                        </Stack>
                                    </FormControl>
                                </>
                            )}

                            <FormControl>
                                <FormLabel>■画像</FormLabel>
                                <Input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                                {formData.image && (
                                    <Box mt={2}>
                                        <Image src={formData.image} alt="DM image" maxH="200px" />
                                        <IconButton
                                            aria-label="画像を削除"
                                            icon={<FaTrash size="20px" />}
                                            size="sm"
                                            colorScheme="red"
                                            onClick={() => setFormData(prev => ({...prev, image: ''}))}
                                            ml={2}
                                        />
                                    </Box>
                                )}
                            </FormControl>

                            <FormControl>
                                <FormLabel>■アラートメッセージ</FormLabel>
                                <Textarea
                                    value={formData.alert_message}
                                    onChange={(e) => setFormData(prev => ({...prev, alert_message: e.target.value}))}
                                    placeholder="アラートメッセージを入力"
                                    rows={2}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>■有効期限</FormLabel>
                                <HStack>
                                    <Input
                                        type="date"
                                        value={formData.start_at}
                                        onChange={(e) => setFormData(prev => ({...prev, start_at: e.target.value}))}
                                    />
                                    <Text>〜</Text>
                                    <Input
                                        type="date"
                                        value={formData.end_at}
                                        onChange={(e) => setFormData(prev => ({...prev, end_at: e.target.value}))}
                                    />
                                </HStack>
                            </FormControl>

                            <Button
                                type="submit"
                                colorScheme="blue"
                                isLoading={isLoading}
                                loadingText={editId ? '更新中...' : '作成中...'}
                            >
                                {editId ? '更新' : '作成'}
                            </Button>
                        </VStack>
                    </form>
                </CardBody>
            </Card>
        </Box>
    );
};

export default InstagramDmCreate;

